import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { conflictData } from '../lib/stock-errors'
import type { ProductVariantSummary } from '@/api/endpoints/product-stock'
import { productsApi } from '@/api/endpoints/products'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ApiError } from '@/api/types'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { formatShare } from '@/shared/lib/stock-quantity'

interface DeleteProductDialogProps {
  sku: string
  productName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Blocked =
  | { kind: 'variants'; variants: Array<ProductVariantSummary> }
  | { kind: 'part'; dishes: Array<{ sku: string; name?: string }> }

/**
 * Soft-delete confirmation. An ordinary product is deleted exactly as before: one `DELETE`
 * without options. CHG-2026-09-stock-usage-and-price-twins (10.7) adds two refusals: a source with
 * variants (`409 product_has_variants`) lists them and offers to delete source and variants
 * together (`withVariants=true`); a part of a live dish (`409 product_is_part`) lists the dishes
 * and cannot be deleted from here.
 */
export function DeleteProductDialog({
  sku,
  productName,
  open,
  onOpenChange,
}: DeleteProductDialogProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const [blocked, setBlocked] = useState<Blocked | null>(null)
  // Each opening is its own session: an answer that arrives after the dialog was closed (Escape
  // while the request ran) belongs to an old session and must not reopen or redirect anything.
  const session = useRef(0)
  useEffect(() => {
    if (open) {
      session.current += 1
      setBlocked(null)
    }
  }, [open])

  const deleteMutation = useMutation({
    mutationFn: (vars: { withVariants: boolean; session: number }) =>
      vars.withVariants
        ? productsApi.delete(sku, { withVariants: true })
        : productsApi.delete(sku),
    onSuccess: (_res, vars) => {
      // The product is gone either way: lists must not keep showing it.
      queryClient.invalidateQueries({ queryKey: ['products'] })
      if (!isCurrent(vars.session)) return
      toast.success(tProducts('toast.deleteSuccess'))
      handleOpenChange(false)
      navigate({ to: '/products' })
    },
    onError: (err, vars) => {
      if (!isCurrent(vars.session)) return
      const withVariants = vars.withVariants
      const variants = conflictData<{
        variants?: Array<ProductVariantSummary>
      }>(err, 'product_has_variants')
      if (variants && !withVariants) {
        setBlocked({ kind: 'variants', variants: variants.variants ?? [] })
        return
      }
      const part = conflictData<{
        dishes?: Array<{ sku: string; name?: string }>
      }>(err, 'product_is_part')
      if (part) {
        setBlocked({ kind: 'part', dishes: part.dishes ?? [] })
        return
      }
      handleOpenChange(false)
      if (err instanceof ApiError) {
        toast.error(getErrorMessage(err.status, err.code))
      } else {
        toast.error(tProducts('toast.deleteFailed'))
      }
    },
  })

  function isCurrent(requestSession: number) {
    return requestSession === session.current
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      // Closing ends the session: whatever is still in flight is answered into the void.
      session.current += 1
      setBlocked(null)
    }
    onOpenChange(next)
  }

  const pending = deleteMutation.isPending

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        {blocked?.kind === 'variants' ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {tProducts('deleteDialog.variantsTitle', { name: productName })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {tProducts('deleteDialog.variantsDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <ul className="space-y-1 rounded-md border px-3 py-2 text-sm">
              {blocked.variants.map((variant) => (
                <li key={variant.sku} className="flex justify-between gap-3">
                  <span>
                    {variant.name}
                    {variant.pausedBySource && (
                      <span className="block text-xs text-muted-foreground">
                        {tProducts('variant.paused')}
                      </span>
                    )}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {variant.sku} ·{' '}
                    {tProducts('variant.shareShort', {
                      share: formatShare(variant.share),
                    })}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground">
              {tProducts('deleteDialog.variantsNote')}
            </p>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={pending}>
                {tCommon('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={pending}
                onClick={(e) => {
                  e.preventDefault()
                  deleteMutation.mutate({
                    withVariants: true,
                    session: session.current,
                  })
                }}
              >
                {pending
                  ? tCommon('deleting')
                  : tProducts('deleteDialog.variantsConfirm', {
                      count: blocked.variants.length,
                    })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : blocked?.kind === 'part' ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {tProducts('deleteDialog.partTitle', { name: productName })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {tProducts('deleteDialog.partDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <ul className="space-y-1 rounded-md border px-3 py-2 text-sm">
              {blocked.dishes.map((dish) => (
                <li key={dish.sku} className="flex justify-between gap-3">
                  <span>{dish.name ?? dish.sku}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {dish.sku}
                  </span>
                </li>
              ))}
            </ul>
            <AlertDialogFooter>
              <AlertDialogCancel>{tCommon('close')}</AlertDialogCancel>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {tProducts('info.deleteTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {tProducts('info.deleteDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={pending}>
                {tCommon('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={pending}
                onClick={(e) => {
                  // Stay open until the answer: a refusal may turn into the variants list.
                  e.preventDefault()
                  deleteMutation.mutate({
                    withVariants: false,
                    session: session.current,
                  })
                }}
              >
                {pending ? tCommon('deleting') : tCommon('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  )
}
